import React, { useState } from 'react';
import { CheckSolidCircleIcon } from '@allergan-data-labs/alle-elements-icons/16/checkSolidCircleIcon';
import type { ChipProps } from './chip-types';
import { ChipGroup } from './chip';

interface FilterChipContextType {
  selectedChips: number[];
  toggleChip: (index: number) => void;
}

const FilterChipContext = React.createContext<FilterChipContextType>({
  selectedChips: [],
  toggleChip: () => undefined,
});

interface FilterChipGroupProps {
  children: React.ReactElement<ChipProps>[];
}

/**
 * Chips can use tags or descriptive words to filter content.
 * They can be a good alternative to toggle buttons or checkboxes.
 * Tapping on a filter chip activates it and appends a leading checkmark icon to the starting edge of the chip label.
 */
export const FilterChipGroup = ({ children }: FilterChipGroupProps) => {
  const [selectedChips, setSelectedChips] = useState<number[]>([]);

  const toggleChip = (index: number) => {
    if (selectedChips.includes(index)) {
      setSelectedChips((prev) => prev.filter((i) => i !== index));
    } else {
      setSelectedChips((prev) => [...prev, index]);
    }
  };

  const chipsArray: ChipProps[] = React.Children.map(
    children,
    (child, index) => {
      if (React.isValidElement<ChipProps>(child)) {
        return {
          ...child.props,
          onClick: () => toggleChip(index),
          icon: selectedChips.includes(index) ? (
            <CheckSolidCircleIcon />
          ) : (
            child.props.icon
          ),
        };
      }
      return null;
    }
  ).filter(Boolean) as ChipProps[];

  return (
    <FilterChipContext.Provider value={{ selectedChips, toggleChip }}>
      <ChipGroup chips={chipsArray} />
    </FilterChipContext.Provider>
  );
};

export { FilterChipContext };
